import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Button,
} from '@mui/material';
import {
  Create as SignatureIcon,
  Psychology as AIIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { signaturenApi, llmApi, einstellungenApi } from '../services/api';
import type { EmailSignatur, LLMProvidersResponse } from '../types/api';
import CalDAVSettings from './CalDAVSettings';

const Einstellungen: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [signaturen, setSignaturen] = useState<EmailSignatur[]>([]);
  const [llmProviders, setLlmProviders] = useState<LLMProvidersResponse | null>(
    null
  );
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [systemPromptSaving, setSystemPromptSaving] = useState(false);
  const [systemPromptSuccess, setSystemPromptSuccess] = useState(false);
  const [n8nServerUrl, setN8nServerUrl] = useState<string>('');
  const [n8nServerUrlSaving, setN8nServerUrlSaving] = useState(false);
  const [n8nServerUrlSuccess, setN8nServerUrlSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [signaturenResponse, providersResponse, systemPromptResponse, n8nServerUrlResponse] =
        await Promise.all([
          signaturenApi.getAll(),
          llmApi.getProviders(),
          einstellungenApi.getByKey('DokumentGenerierung_SystemPrompt'),
          einstellungenApi.getByKey('N8N_ServerUrl'),
        ]);
      setSignaturen(signaturenResponse.data);
      setLlmProviders(providersResponse.data);
      setSystemPrompt(systemPromptResponse.data.wert || '');
      setN8nServerUrl(n8nServerUrlResponse.data.wert || '');
    } catch (err: any) {
      console.error('Einstellungen-Fehler:', err);
      setError(
        err.response?.data?.message || 'Fehler beim Laden der Einstellungen'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetStandardSignatur = async (signaturId: number) => {
    try {
      await signaturenApi.setStandard(signaturId);
      setSignaturen(
        signaturen.map((s) => ({
          ...s,
          istStandard: s.signaturID === signaturId,
        }))
      );
    } catch (err: any) {
      alert('Fehler beim Setzen der Standard-Signatur');
    }
  };

  const handleDeleteSignatur = async (signaturId: number) => {
    if (!window.confirm('Signatur wirklich löschen?')) return;

    try {
      await signaturenApi.delete(signaturId);
      setSignaturen(signaturen.filter((s) => s.signaturID !== signaturId));
    } catch (err: any) {
      alert('Fehler beim Löschen der Signatur');
    }
  };

  const handleSaveSystemPrompt = async () => {
    try {
      setSystemPromptSaving(true);
      setSystemPromptSuccess(false);
      await einstellungenApi.update('DokumentGenerierung_SystemPrompt', { wert: systemPrompt });
      setSystemPromptSuccess(true);
      setTimeout(() => setSystemPromptSuccess(false), 3000);
    } catch (err: any) {
      alert('Fehler beim Speichern des System-Prompts');
    } finally {
      setSystemPromptSaving(false);
    }
  };

  const handleSaveN8nServerUrl = async () => {
    try {
      setN8nServerUrlSaving(true);
      setN8nServerUrlSuccess(false);
      await einstellungenApi.update('N8N_ServerUrl', { wert: n8nServerUrl });
      setN8nServerUrlSuccess(true);
      setTimeout(() => setN8nServerUrlSuccess(false), 3000);
    } catch (err: any) {
      alert('Fehler beim Speichern der N8N Server URL');
    } finally {
      setN8nServerUrlSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Einstellungen</Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={loadData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab icon={<SignatureIcon />} label="Signaturen" />
          <Tab icon={<AIIcon />} label="KI / LLM" />
          <Tab icon={<SettingsIcon />} label="System" />
          <Tab icon={<CalendarIcon />} label="CalDAV" />
        </Tabs>

        <Box p={3}>
          {/* Tab 0: Signaturen */}
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                E-Mail-Signaturen
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Vorschau</TableCell>
                      <TableCell>Erstellt am</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {signaturen.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="textSecondary">
                            Keine Signaturen vorhanden
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      signaturen.map((signatur) => (
                        <TableRow key={signatur.signaturID}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {signatur.name}
                              {signatur.istStandard && (
                                <Chip
                                  label="Standard"
                                  size="small"
                                  color="primary"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {signatur.vorschauText}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {new Date(signatur.erstelltAm).toLocaleDateString(
                                'de-DE'
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" justifyContent="flex-end" gap={1}>
                              <Tooltip
                                title={
                                  signatur.istStandard
                                    ? 'Bereits Standard'
                                    : 'Als Standard setzen'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleSetStandardSignatur(
                                        signatur.signaturID
                                      )
                                    }
                                    disabled={signatur.istStandard}
                                  >
                                    {signatur.istStandard ? (
                                      <StarIcon fontSize="small" color="primary" />
                                    ) : (
                                      <StarBorderIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Löschen">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteSignatur(signatur.signaturID)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 1: KI / LLM */}
          {tabValue === 1 && llmProviders && (
            <Box>
              <Typography variant="h6" gutterBottom>
                KI / LLM Einstellungen
              </Typography>
              <Box mb={3}>
                <Typography variant="subtitle2" color="textSecondary" mb={1}>
                  Aktueller Provider:
                </Typography>
                <Chip
                  label={llmProviders.current}
                  color="primary"
                  icon={<AIIcon />}
                />
              </Box>
              <Typography variant="subtitle2" color="textSecondary" mb={2}>
                Verfügbare Provider:
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                {llmProviders.providers.map((provider) => (
                  <Paper
                    key={provider.name}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {provider.displayName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {provider.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={provider.available ? 'Verfügbar' : 'Nicht verfügbar'}
                      color={provider.available ? 'success' : 'default'}
                      size="small"
                    />
                  </Paper>
                ))}
              </Box>

              {/* System Prompt Einstellung */}
              <Box mt={4}>
                <Typography variant="subtitle2" color="textSecondary" mb={2}>
                  System-Prompt für Dokumentgenerierung:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  placeholder="Geben Sie hier den System-Prompt ein, der bei der KI-gestützten Dokumentgenerierung verwendet wird..."
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <Box display="flex" alignItems="center" gap={2}>
                  <Button
                    variant="contained"
                    startIcon={systemPromptSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleSaveSystemPrompt}
                    disabled={systemPromptSaving}
                  >
                    {systemPromptSaving ? 'Speichere...' : 'Speichern'}
                  </Button>
                  {systemPromptSuccess && (
                    <Alert severity="success" sx={{ py: 0 }}>
                      System-Prompt erfolgreich gespeichert
                    </Alert>
                  )}
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 3 }}>
                Die LLM-Provider-Konfiguration erfolgt über die Backend
                appsettings.json Datei.
              </Alert>
            </Box>
          )}

          {/* Tab 2: System */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Systemeinstellungen
              </Typography>

              {/* N8N Server URL Einstellung */}
              <Box mt={3}>
                <Typography variant="subtitle2" color="textSecondary" mb={2}>
                  N8N Server URL für Ticket-Automation:
                </Typography>
                <TextField
                  fullWidth
                  value={n8nServerUrl}
                  onChange={(e) => setN8nServerUrl(e.target.value)}
                  placeholder="http://localhost:5678/webhook/ticket-automation"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  helperText="URL des N8N MCP Servers für automatische Ticket-Ausführung"
                />
                <Box display="flex" alignItems="center" gap={2}>
                  <Button
                    variant="contained"
                    startIcon={n8nServerUrlSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleSaveN8nServerUrl}
                    disabled={n8nServerUrlSaving}
                  >
                    {n8nServerUrlSaving ? 'Speichere...' : 'Speichern'}
                  </Button>
                  {n8nServerUrlSuccess && (
                    <Alert severity="success" sx={{ py: 0 }}>
                      N8N Server URL erfolgreich gespeichert
                    </Alert>
                  )}
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 3 }}>
                Die N8N Server URL kann hier zentral konfiguriert werden. Änderungen werden sofort wirksam.
              </Alert>
            </Box>
          )}

          {/* Tab 3: CalDAV */}
          {tabValue === 3 && (
            <Box>
              <CalDAVSettings />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Einstellungen;
